/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { DocumentsListFilterParams, DocumentsListItemDto } from '../api/documents/documentsClient.typings';
import { DocumentsAction, DocumentsActionType } from '../actions/documentsActions';
import { DocumentsListTableData } from '../../components/RegistryWidgets/Documents/DocumentsList/DocumentsListConfig';
import { today } from './CashBoxRegistry/cashboxDetailsReducer';
import moment from 'moment';
import {
    CommonlyUsedOptions,
    QuickSelectUnits,
    TimeRangeFilterParams,
    TimeRangeFilterType,
} from '../../components/RegistryWidgets/Common/TimeRangeFilter';
import { getPageLimitByScreenSize } from '../../utils/utils';

export const defaultSearchType: keyof DocumentsListTableData = 'cashBoxTaxNumber';
export const defaultDocNumbersSearchType: keyof DocumentsListTableData = 'globalDocumentNumber';
export const defaultRestoredFilterValue: boolean = null;

export interface DocumentsState {
    documents: DocumentsListItemDto[];
    filterParams: DocumentsListFilterParams;
    timeRangeFilterParams: TimeRangeFilterParams;
    changedFilterParams: DocumentsListFilterParams;
    changedTimeRangeFilterParams: TimeRangeFilterParams;
    limit: number;
    afterKeyCreatedAtUtc: string;
    afterKeyId: string;
    lastRequestDocumentsCount: number;
    isLoading: boolean;
    isCardView: boolean;
    isDataIncomplete: boolean;
}

const initialState: DocumentsState = {
    documents: [],
    filterParams: {
        search_type: defaultSearchType,
        search_text: '',
        docNumbersSearchType: defaultDocNumbersSearchType,
        docNumbersSearchText: '',
        dateFrom: today.utc().clone().subtract(0, 'day').format(),
        dateTo: moment(new Date()).endOf('day').utc().format(),
        taxRates: [],
        paymentTypes: [],
        sumFrom: null,
        sumTo: null,
        docTypeIds: [],
        restored: defaultRestoredFilterValue,
    },
    changedFilterParams: null,
    timeRangeFilterParams: {
        shownFilter: TimeRangeFilterType.QUICK_SELECT,
        showDates: {
            dateFrom: today.utc().clone().subtract(0, 'day').format(),
            dateTo: moment(new Date()).endOf('day').utc().format(),
        },
        quickSelect: {
            value: 1,
            units: QuickSelectUnits.Days,
        },
        commonlyUsed: CommonlyUsedOptions.Today,
    },
    changedTimeRangeFilterParams: null,
    limit: getPageLimitByScreenSize(),
    afterKeyCreatedAtUtc: null,
    afterKeyId: null,
    lastRequestDocumentsCount: null,
    isLoading: false,
    isCardView: false,
    isDataIncomplete: false,
};

function documentsReducer(state: DocumentsState = initialState, action: DocumentsAction): DocumentsState {
    switch (action.type) {
        case DocumentsActionType.OPEN_REGISTRY_DOCUMENTS_LIST_PAGE:
            return {
                ...state,
            };

        case DocumentsActionType.FETCH_NEXT_PAGE_START:
            return {
                ...state,
                isLoading: true,
                changedFilterParams: null,
                changedTimeRangeFilterParams: null,
            };

        case DocumentsActionType.FETCH_EXTENDED_DATA_START:
            return {
                ...state,
                isLoading: true,
            };

        case DocumentsActionType.EXTENDED_DATA_IS_COMPLETE:
            return {
                ...state,
                isDataIncomplete: false,
            };

        case DocumentsActionType.CHANGE_TIME_RANGE_FILTER_PARAMS:
            return {
                ...state,
                changedTimeRangeFilterParams: action.payload,
            };

        case DocumentsActionType.CHANGE_FILTERS:
            return {
                ...state,
                changedFilterParams: action.payload,
            };

        case DocumentsActionType.APPLY_FILTERS:
            return {
                ...state,
                isLoading: true,

                documents: [],
                afterKeyCreatedAtUtc: null,
                afterKeyId: null,
                lastRequestDocumentsCount: null,

                filterParams: state.changedFilterParams ? { ...state.changedFilterParams } : { ...state.filterParams },
                timeRangeFilterParams: state.changedTimeRangeFilterParams
                    ? { ...state.changedTimeRangeFilterParams }
                    : { ...state.timeRangeFilterParams },
                changedFilterParams: null,
                changedTimeRangeFilterParams: null,
            };

        case DocumentsActionType.CHANGE_CURRENT_VIEW:
            return {
                ...state,
                isCardView: action.payload,
            };

        case DocumentsActionType.FETCH_DOCUMENTS_PAGE_SUCCESS:
            const combinedList: DocumentsListItemDto[] = [...state.documents];
            let isDataIncomplete = false;
            if (action.payload.list.documents && action.payload.list.documents.length > 0) {
                combinedList.push(...action.payload.list.documents);
                isDataIncomplete = true;
            }
            return {
                ...state,
                documents: combinedList,
                afterKeyCreatedAtUtc: action.payload.list.afterKeyCreatedAtUtc,
                afterKeyId: action.payload.list.afterKeyId,
                lastRequestDocumentsCount: action.payload.list.documents.length,
                isLoading: false,
                isDataIncomplete,
            };

        case DocumentsActionType.FETCH_DOCUMENTS_PAGE_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case DocumentsActionType.FETCH_EXTENDED_DATA_SUCCESS:
            const updatedDocuments: DocumentsListItemDto[] = [...state.documents];

            const dataMap = new Map<string, string>();
            if (action.payload && action.payload.cashBoxes && action.payload.cashBoxes.length > 0)
                action.payload.cashBoxes.forEach((item) => dataMap.set(item.taxNumber, item.companyName));

            updatedDocuments.forEach((document) => {
                if (document.companyName === null || document.companyName === undefined) {
                    const companyName = dataMap.get(document.cashBoxTaxNumber);
                    if (companyName !== undefined) document.companyName = companyName;
                }
            });

            return {
                ...state,
                documents: updatedDocuments,
                isLoading: false,
                isDataIncomplete: false,
            };

        case DocumentsActionType.FETCH_EXTENDED_DATA_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case DocumentsActionType.DOWNLOAD_EXCEL_START:
            return {
                ...state,
                isLoading: true,
                changedFilterParams: null,
                changedTimeRangeFilterParams: null,
            };

        case DocumentsActionType.DOWNLOAD_EXCEL_SUCCESS:
        case DocumentsActionType.DOWNLOAD_EXCEL_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case DocumentsActionType.EXIT_REGISTRY_DOCUMENTS_LIST_PAGE:
            return {
                ...state,
                changedFilterParams: null,
                changedTimeRangeFilterParams: null,
            };

        default:
            return state;
    }
}

export default documentsReducer;
